/**
 * ZAXIA Staff Layout - Sidebar Component
 * UI-17: Navigation Role Separation (Control Tower Architecture)
 *
 * Three navigation tiers:
 *   PRIMARY:       Control Tower — the product (visually dominant)
 *   INVESTIGATION: Rooms, Housekeeping, Requests — secondary drill-down
 *   SUPPORT:       Bookings, Messaging, Green Stay, AI Assistant, PMS Sandbox
 *                  — collapsible tools/simulation group
 *   SYSTEM:        Settings (footer)
 *
 * Principle: Control Tower IS the product. Everything else is supporting context.
 */

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

import type { SystemSeverity } from './deriveSystemSeverity';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

// ═══ INVESTIGATION — Drill-down surfaces (secondary) ═══
const investigationItems: NavItem[] = [
  {
    // OPS-36: Real-time operational guidance feed (mobile-first)
    label: 'Ops Feed',
    path: '/ops-feed',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    label: 'Rooms',
    path: '/rooms',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    label: 'Housekeeping',
    path: '/housekeeping',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    label: 'Requests',
    path: '/requests',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
      </svg>
    ),
  },
];

// ═══ SUPPORT — Tools & Simulation (collapsible) ═══
const supportItems: NavItem[] = [
  {
    label: 'Bookings',
    path: '/bookings',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    label: 'Messaging',
    path: '/ai-assistant',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-4 4z" />
      </svg>
    ),
  },
  {
    label: 'Green Stay',
    path: '/green',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm8 10V7m-4 5l4-5 4 5" />
      </svg>
    ),
  },
  {
    label: 'PMS Sandbox',
    path: '/pms-sandbox',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
  },
  {
    label: 'Ops Sandbox',
    path: '/ops/sandbox',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  openMaintenanceCount?: number;
  systemSeverity?: SystemSeverity;
}

export function Sidebar({ isOpen, onClose, openMaintenanceCount = 0, systemSeverity = 'normal' }: SidebarProps) {
  const { user } = useAuth();
  const [supportExpanded, setSupportExpanded] = useState(false);

  // Role-based visibility: Sandbox & AI tools hidden for non-admin
  const visibleSupportItems = supportItems.filter(item => {
    if (item.path === '/pms-sandbox' || item.path === '/ai-assistant' || item.path === '/ops/sandbox') {
      return user?.role === 'admin';
    }
    return true;
  });

  // ─── Investigation item renderer (muted, smaller) ───
  const renderInvestigationItem = (item: NavItem) => (
    <li key={item.path}>
      <NavLink
        to={item.path}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
            isActive
              ? 'bg-slate-700 text-slate-100'
              : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
          }`
        }
      >
        {item.icon}
        <span className="flex-1">{item.label}</span>
        {item.path === '/requests' && openMaintenanceCount > 0 && (
          <span className="text-[10px] font-bold bg-slate-600 text-slate-200 px-1.5 py-0.5 rounded-full min-w-[1.1rem] text-center">
            {openMaintenanceCount}
          </span>
        )}
      </NavLink>
    </li>
  );

  // ─── Support item renderer (most muted) ───
  const renderSupportItem = (item: NavItem) => (
    <li key={item.path}>
      <NavLink
        to={item.path}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
            isActive
              ? 'bg-slate-700/50 text-slate-300'
              : 'text-slate-500 hover:bg-slate-700/30 hover:text-slate-400'
          }`
        }
      >
        {item.icon}
        <span>{item.label}</span>
      </NavLink>
    </li>
  );

  return (
    <aside
      className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-64 bg-slate-800 border-r border-slate-700 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:transform-none
      `}
    >
      {/* Mobile Header with Close Button */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700">
        <span className="text-white font-semibold">Menu</span>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* ═══ PRIMARY — Control Tower (the product) ═══ */}
        <div className="px-3 mb-6">
          <NavLink
            to="/control-tower"
            onClick={onClose}
            className={({ isActive }) => {
              const base = 'flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm font-semibold transition-all';
              if (!isActive) {
                // Inactive hover reflects current severity
                if (systemSeverity === 'critical') return `${base} text-white bg-slate-700/60 hover:bg-rose-600/80 hover:shadow-md`;
                if (systemSeverity === 'warning') return `${base} text-white bg-slate-700/60 hover:bg-amber-600/80 hover:shadow-md`;
                return `${base} text-white bg-slate-700/60 hover:bg-emerald-600/80 hover:shadow-md`;
              }
              // Active styling varies by severity
              if (systemSeverity === 'critical') return `${base} bg-rose-600 text-white shadow-lg shadow-rose-900/40 ring-1 ring-rose-500/30`;
              if (systemSeverity === 'warning') return `${base} bg-amber-600 text-white shadow-lg shadow-amber-900/40 ring-1 ring-amber-500/30`;
              return `${base} bg-emerald-600 text-white shadow-lg shadow-emerald-900/40 ring-1 ring-emerald-500/30`;
            }}
          >
            {/* Icon swaps to alert triangle in critical state — accessible without color/motion */}
            {systemSeverity === 'critical' ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )}
            <span className="flex-1">Control Tower</span>
            {/* Severity indicator — only shows when system needs attention */}
            {systemSeverity === 'critical' && (
              <span className="w-2 h-2 rounded-full bg-rose-300 animate-pulse" />
            )}
            {systemSeverity === 'warning' && (
              <span className="w-2 h-2 rounded-full bg-amber-300" />
            )}
            {/* normal = no indicator — calm, stable, no motion */}
          </NavLink>
        </div>

        {/* ═══ INVESTIGATION — Drill-down surfaces ═══ */}
        <div className="px-3">
          <p className="px-3 mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            Investigate
          </p>
          <ul className="space-y-0.5">
            {investigationItems.map(renderInvestigationItem)}
          </ul>
        </div>

        {/* ═══ SUPPORT — Tools / Simulation (collapsible) ═══ */}
        <div className="mt-6 px-3">
          <button
            type="button"
            onClick={() => setSupportExpanded(prev => !prev)}
            className="w-full flex items-center justify-between px-3 mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium hover:text-slate-400 transition-colors"
          >
            <span>Tools / Simulation</span>
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${supportExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {supportExpanded && (
            <ul className="space-y-0.5">
              {visibleSupportItems.map(renderSupportItem)}
            </ul>
          )}
        </div>
      </nav>

      {/* Footer — Guide + Settings + version */}
      <div className="border-t border-slate-700">
        <ul className="px-3 py-2">
          <li>
            <NavLink
              to="/guide"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-700 text-slate-200'
                    : 'text-slate-500 hover:bg-slate-700/50 hover:text-slate-400'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span>Staff Guide</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/settings"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-700 text-slate-200'
                    : 'text-slate-500 hover:bg-slate-700/50 hover:text-slate-400'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Settings</span>
            </NavLink>
          </li>
        </ul>
        <p className="px-4 pb-4 text-slate-600 text-[10px] text-center">
          ZAXIA Operations v1.0
        </p>
      </div>
    </aside>
  );
}
